import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { theme } from '@garnish/constants';
import { BodyText, TextLinkifyTags, useResponsive } from '@sg/garnish';

export const LoyaltyTerms = (props: LoyaltyTermsProps) => {
  const { trademark, termsAndConditionsUrl, palette, onLinkPress } = props;

  const { currentBreakpoint } = useResponsive();

  const { formatMessage } = useIntl();
  const links = useMemo(
    () => [
      {
        tag: 'terms',
        description: formatMessage(messages.termsTag),
        href: termsAndConditionsUrl,
        underline: true,
        onPress: onLinkPress,
      },
    ],
    [termsAndConditionsUrl, formatMessage, onLinkPress],
  );

  return (
    <View style={styles.wrapper}>
      <BodyText
        style={[textPalettes[palette], styles.text]}
        sizeMatch={['12', '22']}
      >
        <TextLinkifyTags
          palette={textLinkPalettes[palette]}
          sizeMatch={['12', '22']}
          links={links}
          style={styles.text}
        >
          {formatMessage(messages.terms, { terms: '{terms}' })}
        </TextLinkifyTags>
      </BodyText>

      {currentBreakpoint.isXS && trademark ? (
        <BodyText
          style={[textPalettes[palette], styles.text]}
          sizeMatch={['12', '22']}
        >
          {trademark}
        </BodyText>
      ) : null}
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  termsTag: {
    defaultMessage: 'terms',
    description: 'Loyalty Home | Footer | subtitle',
  },
  terms: {
    defaultMessage: 'See full {terms} for more details',
    description: 'Loyalty Home | Footer | subtitle',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  lightLabel: {
    color: theme.colors.CREAM,
  },
  darkLabel: {
    color: theme.colors.DARK_KALE,
  },
  wrapper: {
    gap: theme.spacing['4'],
  },
  text: {
    textAlign: 'center',
  },
});

const textPalettes = {
  light: styles.lightLabel,
  dark: styles.darkLabel,
};

const textLinkPalettes = {
  light: 'sweet-corn' as const,
  dark: 'kale' as const,
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyTermsProps = {
  trademark?: string;
  termsAndConditionsUrl: string;
  palette: 'dark' | 'light';
  onLinkPress?: () => void;
};
